import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

export type Document = Database['public']['Tables']['documents']['Row'];
type DocumentInput = Omit<Document, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'number' | 'content'>;

type DocumentsContextType = {
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  addDocument: (document: DocumentInput) => Promise<Document>;
  updateDocument: (id: string, document: Partial<Document>) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  getDocument: (id: string) => Document | undefined;
};

const DocumentsContext = createContext<DocumentsContextType | undefined>(undefined);

export const DocumentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchDocuments();
    } else {
      setDocuments([]);
      setIsLoading(false);
      setError(null);
    }
  }, [user]);

  const fetchDocuments = async () => {
    try {
      setError(null);
      setIsLoading(true);

      if (!supabase) {
        throw new Error('Database client not initialized');
      }

      const { data, error: supabaseError } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (supabaseError) {
        throw supabaseError;
      }

      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateDocumentNumber = async (type: 'invoice' | 'quote'): Promise<string> => {
    const prefix = type === 'invoice' ? user?.invoice_prefix || 'INV-' : user?.quote_prefix || 'QUO-';
    const nextNumber = type === 'invoice' ? user?.next_invoice_number || 1 : user?.next_quote_number || 1;
    return `${prefix}${String(nextNumber).padStart(3, '0')}`;
  };

  const addDocument = async (documentData: DocumentInput): Promise<Document> => {
    if (!user) throw new Error('User not authenticated');
    if (!supabase) throw new Error('Database client not initialized');

    try {
      setError(null);
      const number = await generateDocumentNumber(documentData.type);
      
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('name, company')
        .eq('id', documentData.client_id)
        .single();

      if (clientError || !clientData) {
        throw new Error('Client not found');
      }

      const { data, error } = await supabase
        .from('documents')
        .insert({
          ...documentData,
          user_id: user.id,
          number,
          content: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      setDocuments(prev => [data, ...prev]);
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add document';
      console.error('Error adding document:', error);
      setError(errorMessage);
      throw error;
    }
  };

  const updateDocument = async (id: string, documentData: Partial<Document>) => {
    if (!user) throw new Error('User not authenticated');
    if (!supabase) throw new Error('Database client not initialized');

    try {
      setError(null);
      const { error } = await supabase
        .from('documents')
        .update({
          ...documentData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      // Update local state immediately
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === id ? { ...doc, ...documentData } : doc
        )
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update document';
      console.error('Error updating document:', error);
      setError(errorMessage);
      throw error;
    }
  };

  const deleteDocument = async (id: string) => {
    if (!user) throw new Error('User not authenticated');
    if (!supabase) throw new Error('Database client not initialized');

    try {
      setError(null);
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      setDocuments(prev => prev.filter(document => document.id !== id));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete document';
      console.error('Error deleting document:', error);
      setError(errorMessage);
      throw error;
    }
  };

  const getDocument = (id: string) => {
    return documents.find(document => document.id === id);
  };

  return (
    <DocumentsContext.Provider 
      value={{ 
        documents, 
        isLoading,
        error,
        addDocument, 
        updateDocument, 
        deleteDocument, 
        getDocument,
      }}
    >
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = (): DocumentsContextType => {
  const context = useContext(DocumentsContext);
  if (context === undefined) {
    throw new Error('useDocuments must be used within a DocumentsProvider');
  }
  return context;
};